/**
 * Wohin die Kopien beim Duplizieren rücken (⌘/Strg + D).
 *
 * Bewusst ohne React-Bezug, wie `canvasAlignment.ts` und `canvasViewport.ts`:
 * die Rechnung soll ohne gerenderten Canvas prüfbar sein.
 */

import { ALIGNMENT_GAP, getBoundingBox, type MovingRect } from './canvasAlignment'

/**
 * Abstand zwischen Original und Kopie.
 *
 * Derselbe Wert, auf den das **Einrasten** beim Anlegen neben eine fremde
 * Kante rastet — eine Kopie liegt also genau so weit weg, wie ein von Hand
 * danebengeschobener Notiz einrasten würde. Angenehmer Nebeneffekt bei der
 * Vorgabegröße: 168 + 24 = 192 = 8 × 24, die Kopie landet wieder auf dem
 * **Punktraster**, wenn das Original darauf lag.
 */
export const DUPLICATE_GAP = ALIGNMENT_GAP

/**
 * Wie weit der Zeiger gewandert sein muss, bevor das Alt-Ziehen eine Kopie
 * anlegt — in BILDSCHIRM-Pixeln, wie `ALIGNMENT_THRESHOLD`, also gegen den
 * Zoom zu rechnen. Sonst löste die Geste herausgezoomt bei drei Pixeln aus und
 * hereingezoomt erst bei vierundzwanzig.
 *
 * Die Schwelle ist kein Feinschliff, sondern der Schutz vor einem Alt-KLICK:
 * ohne sie entstünden deckungsgleiche, unsichtbare Kopien, und im Canvas gibt
 * es kein Rückgängig. 12 liegt sicher jenseits des Handzitterns und fühlt sich
 * noch sofort an; nebenbei ist es genau der Punkt, an dem sich beim Ziehen
 * einer Mehrfachauswahl ohnehin zum ersten Mal etwas bewegt (der Versatz
 * rastet dort auf `GRID` = 24).
 */
export const DUPLICATE_DRAG_THRESHOLD = 12

/**
 * Ob `a` dem Rechteck `b` zu nahe kommt — überlappend ODER näher als der
 * übliche Abstand.
 *
 * Der Abstand gehört in die Prüfung, nicht nur die Überlappung: Eine Kopie, die
 * sich mit einer Ecke an einen fremden Notizen schmiegt, sieht aus wie ein
 * Versehen, auch wenn sich formal nichts überdeckt. Auf einer unbegrenzten
 * Fläche gibt es keinen Grund, so eng zu werden.
 *
 * Genau `DUPLICATE_GAP` Abstand ist erlaubt — das ist ja der Abstand, den das
 * Duplizieren selbst herstellt.
 */
export function tooClose(a: MovingRect, b: MovingRect): boolean {
  return (
    a.x < b.x + b.width + DUPLICATE_GAP &&
    a.x + a.width + DUPLICATE_GAP > b.x &&
    a.y < b.y + b.height + DUPLICATE_GAP &&
    a.y + a.height + DUPLICATE_GAP > b.y
  )
}

/**
 * Um wie viel die Kopien rücken — nach rechts (positiv) oder nach links
 * (negativ).
 *
 * **Gesucht wird der nächste FREIE Platz**, abwechselnd nach beiden Seiten und
 * mit wachsendem Abstand: ein Schritt nach rechts, einer nach links, zwei nach
 * rechts, zwei nach links … Rechts hat bei gleichem Abstand den Vorrang — die
 * Richtung, die man erwartet und die FigJam und Miro nehmen.
 *
 * „Frei" heißt dabei nicht nur „überlappt nicht", sondern **hält den üblichen
 * Abstand** (siehe `tooClose`). Sonst schmiegte sich eine Kopie mit einer Ecke
 * an einen fremden Notizen, und das sieht aus wie ein Versehen.
 *
 * Auf einer unbegrenzten Fläche gibt es immer einen freien Platz, also wird
 * auch immer einer gefunden: Jenseits des äußersten Hindernisses ist zwangs-
 * läufig Ruhe. Eine Kopie auf einem vorhandenen Notizen abzulegen wäre nie
 * richtig — man sähe sie nicht und merkte es erst beim Wegziehen.
 *
 * Der Betrag eines Schritts kommt aus dem umschließenden Rechteck der GANZEN
 * Auswahl, nicht je Notiz aus seiner eigenen Breite. Der Unterschied zählt,
 * sobald mehr als einer ausgewählt ist: Bei einzelnen Versätzen zerfiele die
 * Anordnung, weil unterschiedlich breite Notizen unterschiedlich weit
 * wanderten — zwei untereinanderliegende Kopien stünden dann nicht mehr
 * untereinander. Der gemeinsame Versatz hält die Gruppe zusammen und stellt
 * zugleich sicher, dass sie das Original nicht überdeckt.
 *
 * @param others Die Notizen, die im Weg stehen können — alle NICHT
 *               ausgewählten. Die Originale selbst brauchen nicht dabei zu
 *               sein: Der Versatz umfasst die ganze Auswahl, die Kopien
 *               überdecken sie also ohnehin nie.
 */
export function getDuplicateOffset(selected: MovingRect[], others: MovingRect[] = []): number {
  const box = getBoundingBox(selected)
  if (!box) return 0

  const distance = box.right - box.left + DUPLICATE_GAP
  if (others.length === 0) return distance

  const frei = (offset: number) =>
    selected.every(rect =>
      others.every(other => !tooClose({ ...rect, x: rect.x + offset }, other))
    )

  // So viele Schritte, dass die Auswahl garantiert an ALLEN Hindernissen
  // vorbei ist — dort kann nichts mehr kollidieren. Die Suche endet also immer,
  // ohne auf eine willkürliche Obergrenze angewiesen zu sein.
  const outermost = Math.max(
    ...others.map(o => Math.abs(o.x + o.width - box.left)),
    ...others.map(o => Math.abs(o.x - box.right)),
  )
  // Der Abstand zählt mit: „vorbei" heißt hier nicht bündig, sondern
  // `DUPLICATE_GAP` daneben.
  const maxSteps = Math.ceil((outermost + DUPLICATE_GAP) / distance) + 1

  for (let step = 1; step <= maxSteps; step++) {
    if (frei(distance * step)) return distance * step
    if (frei(-distance * step)) return -distance * step
  }

  // Unerreichbar, solange `others` endlich ist: Spätestens bei `maxSteps` liegt
  // die Auswahl jenseits jedes Hindernisses. Als Rückfall die erwartete
  // Richtung, damit die Funktion auch dann eine Zahl liefert.
  return distance
}
